import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import Iconify from "src/components/iconify/Iconify";

function UploadTenantLogoField({ logo, setLogo }) {
  const [logoPreview, setLogoPreview] = useState("");
  const [uploadError, setUploadError] = useState("");

  const acceptedFileTypes = useMemo(
    () => ({
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "image/svg+xml": [".svg"],
    }),
    [],
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;
      setUploadError("");
      setLogo(file);
    },
    [setLogo],
  );

  const onDropRejected = useCallback((fileRejections) => {
    const firstError = fileRejections?.[0]?.errors?.[0];
    if (!firstError) return;

    if (firstError.code === "file-too-large") {
      setUploadError("Die Datei ist zu groß. Bitte maximal 5 MB hochladen.");
      return;
    }

    if (firstError.code === "file-invalid-type") {
      setUploadError(
        "Ungültiges Format. Erlaubt sind PNG, JPG, JPEG, WEBP und SVG.",
      );
      return;
    }

    setUploadError("Die Datei konnte nicht verarbeitet werden.");
  }, []);

  useEffect(() => {
    if (!logo) {
      setLogoPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(logo);
    setLogoPreview(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [logo]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptedFileTypes,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    multiple: false,
  });

  const handleClearSelection = () => {
    setUploadError("");
    setLogo(null);
  };

  const fileSizeLabel = logo?.size
    ? `${(logo.size / 1024 / 1024).toFixed(2)} MB`
    : "";

  return (
    <Stack spacing={2}>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "divider",
          borderRadius: 2,
          py: 4,
          px: 3,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: isDragActive ? "action.hover" : "background.paper",
          transition: "all 0.2s ease",
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={1} alignItems="center">
          <Iconify
            icon={isDragActive ? "eva:download-fill" : "eva:cloud-upload-fill"}
            width={32}
          />
          <Typography variant="subtitle1" fontWeight={700}>
            {isDragActive
              ? "Datei hier ablegen"
              : "Logo hierher ziehen oder anklicken"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            PNG, JPG, JPEG, WEBP, SVG - max. 5 MB
          </Typography>
        </Stack>
      </Box>

      {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}

      {logo ? (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1}
            >
              <Box>
                <Typography variant="subtitle2">{logo.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fileSizeLabel}
                </Typography>
              </Box>
              <Button
                size="small"
                color="inherit"
                startIcon={<Iconify icon="eva:close-fill" width={18} />}
                onClick={handleClearSelection}
              >
                Auswahl entfernen
              </Button>
            </Stack>
            {logoPreview ? (
              <Box
                component="img"
                src={logoPreview}
                alt="Logo Vorschau"
                sx={{
                  width: "auto",
                  maxHeight: 96,
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            ) : null}
          </Stack>
        </Box>
      ) : (
        <Alert severity="info">
          Noch kein neues Logo ausgewählt. Wählen Sie eine Datei aus, um die
          Vorschau zu sehen.
        </Alert>
      )}
    </Stack>
  );
}

UploadTenantLogoField.propTypes = {
  logo: PropTypes.object,
  setLogo: PropTypes.func.isRequired,
};

export default UploadTenantLogoField;
